import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './HeroSearchBar.css';
import { getAnnouncementUrl } from '../../utils/slugify';
import { getApiEndpoint, getUploadUrl } from '../../config/api';

const HeroSearchBar = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [allAnnouncements, setAllAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const searchRef = useRef(null);
    const navigate = useNavigate();

    // Fetch all announcements once on mount
    useEffect(() => {
        fetchAllAnnouncements();
    }, []);

    const fetchAllAnnouncements = async () => {
        try {
            const response = await fetch(getApiEndpoint('/api/announcements/all'));
            const data = await response.json();
            
            if (data.success && data.announcements) {
                setAllAnnouncements(data.announcements);
            }
        } catch (error) {
            console.error('Error loading announcements:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                closeDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Client-side search with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.trim().length > 0) {
                performClientSideSearch(searchQuery);
            } else {
                setSearchResults([]);
                closeDropdown();
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, allAnnouncements]); // eslint-disable-line react-hooks/exhaustive-deps

    const closeDropdown = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowDropdown(false);
            setIsClosing(false);
        }, 250); // Match the animation duration
    };

    const performClientSideSearch = (query) => {
        const lowerQuery = query.toLowerCase().trim();
        // Normalize by removing spaces for better matching (e.g., "JS45" matches "JS 45")
        const normalizedQuery = lowerQuery.replace(/\s+/g, '');
        
        const results = allAnnouncements.filter(announcement => {
            // Search in multiple fields
            const searchableText = [
                announcement.title,
                announcement.title_en,
                announcement.title_ro,
                announcement.manufacturer,
                announcement.model,
                announcement.category,
                announcement.subcategory,
                announcement.description,
                announcement.description_en,
                announcement.description_ro,
                announcement.location
            ].filter(Boolean).join(' ').toLowerCase();
            
            // Normalize searchable text by removing spaces too
            const normalizedText = searchableText.replace(/\s+/g, '');
            
            return normalizedText.includes(normalizedQuery);
        });
        
        setSearchResults(results);
        setShowDropdown(results.length > 0);
    };

    const handleResultClick = (announcement) => {
        navigate(getAnnouncementUrl(announcement.id, announcement.title, announcement.category, announcement.subcategory));
        setSearchQuery('');
        closeDropdown();
        setSearchResults([]);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        closeDropdown();
    };

    const formatPrice = (price) => {
        if (!price) return 'Price on request';
        return `€${parseInt(price).toLocaleString()}`;
    };

    const getImageUrl = (result) => {
        // Get first image from images field or use main_image
        if (result.images) {
            const imageArray = result.images.split(',').map(img => img.trim());
            if (imageArray.length > 0 && imageArray[0]) {
                return getUploadUrl(imageArray[0]);
            }
        }
        if (result.main_image) {
            return getUploadUrl(result.main_image);
        }
        return null;
    };

    return (
        <div className="hero-search-bar" ref={searchRef}>
            <div className="hero-search-wrapper">
                <div className="hero-search-icon">
                    <FaSearch />
                </div>
                <input
                    type="text"
                    className="hero-search-input"
                    placeholder="Caută excavatoare, încărcătoare, compactoare..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                        if (searchResults.length > 0) {
                            setShowDropdown(true);
                        }
                    }}
                />
                {searchQuery && (
                    <button className="hero-search-clear" onClick={handleClearSearch}>
                        <FaTimes />
                    </button>
                )}
                {isLoading && (
                    <div className="hero-search-spinner">
                        <div className="spinner"></div>
                    </div>
                )}
            </div>

            {/* Search Results Dropdown */}
            {showDropdown && searchResults.length > 0 && (
                <div className={`hero-search-dropdown ${isClosing ? 'closing' : ''}`}>
                    <div className="hero-dropdown-header">
                        <span className="results-count">
                            {searchResults.length} {searchResults.length === 1 ? 'rezultat găsit' : 'rezultate găsite'}
                        </span>
                    </div>
                    <div className="hero-dropdown-results">
                        {searchResults.map((result) => {
                            const imageUrl = getImageUrl(result);
                            return (
                                <div
                                    key={result.id}
                                    className="hero-result-item"
                                    onClick={() => handleResultClick(result)}
                                >
                                    <div className="hero-result-image-wrapper">
                                        {imageUrl ? (
                                            <img 
                                                src={imageUrl} 
                                                alt={result.title_en || result.title}
                                                className="hero-result-img"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div className="hero-no-image" style={{ 
                                            display: imageUrl ? 'none' : 'flex'
                                        }}>
                                            🚜
                                        </div>
                                    </div>
                                    <div className="hero-result-content">
                                        <h4 className="hero-result-title">
                                            {result.title_en || result.title}
                                        </h4>
                                        <div className="hero-result-info">
                                            <span className="hero-result-manufacturer">
                                                {result.manufacturer} {result.model}
                                            </span>
                                            <div className="hero-result-meta">
                                                {result.year && (
                                                    <span className="hero-result-year">
                                                        {result.year}
                                                    </span>
                                                )}
                                                {result.hours && (
                                                    <span className="hero-result-hours">
                                                        {result.hours}h
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="hero-result-price">
                                            {formatPrice(result.price)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {searchResults.length > 6 && (
                        <div className="hero-dropdown-footer">
                            <button 
                                className="hero-view-all-btn"
                                onClick={() => {
                                    navigate(`/machines?search=${encodeURIComponent(searchQuery)}`);
                                    closeDropdown();
                                }}
                            >
                                Vezi toate {searchResults.length} rezultatele
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* No Results Message */}
            {showDropdown && searchQuery && searchResults.length === 0 && !isLoading && (
                <div className={`hero-search-dropdown ${isClosing ? 'closing' : ''}`}>
                    <div className="hero-dropdown-header">
                        <span className="results-count">0 rezultate găsite</span>
                    </div>
                    <div className="hero-no-results">
                        <div className="no-results-icon">🔍</div>
                        <p className="no-results-text">Nu am găsit rezultate pentru "{searchQuery}"</p>
                        <p className="no-results-hint">Încearcă să cauți:</p>
                        <ul className="no-results-suggestions">
                            <li>Termeni mai generali (ex: "excavator" în loc de "excavator CAT 320D")</li>
                            <li>Verifica ortografia și diacriticile</li>
                            <li>Marca sau modelul utilajului</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HeroSearchBar;
